import { playErrorFeedback, showsFailImage } from '../utils/scanFeedback';
import {
  SET_SCAN_PACK_CURRENT_ORDER,
  SET_SCAN_PACK_BARCODE_INPUT,
  SET_SCAN_PACK_MAIN_INPUT_FOCUS,
  SET_SCAN_PACK_CURRENT_ITEM,
  SET_SCAN_PACK_PROMPT,
  SET_SCAN_PACK_SCAN_SUCCESS,
  SET_SCAN_PACK_SCAN_ERROR,
  SET_TYPE_SCAN_STATE,
  SET_TYPE_SCAN_ADJACENT_SCANS,
  SET_ACTIVE_BOX_INDEX,
  SET_MULTI_BOX_ALERT,
  SET_PRINT_SLIP_NOTICE,
  CLEAR_PRINT_SLIP_NOTICE,
  SET_ORDER_COMPLETE,
  SET_ORDER_COMPLETE_ACTION,
  SET_POST_SCAN_PIPELINE,
  SET_POST_SCAN_TRACKING_INPUT,
  SET_ALIAS_STATE,
  SET_NAVIGATION_SOURCE,
  ADD_SCAN_PACK_LOCAL_LOG,
  SET_SCAN_PACK_LOADING,
  SET_WMS_ZERO_PENDING,
  SET_BOX_CHAMP_STATE,
  RESET_BOX_CHAMP_STATE
} from '../constants';
import {
  restoreNotFulfilledItems,
  ensureOrderBoxFields,
  shuffleItemToFront,
  getFailTimeout
} from '../utils/orderHelpers';
import { getFirstItem } from '../components/ScanpackScanning/utils/scanningUtils';
import { guidedHandoffOwnsFocus } from '../components/ScanpackScanning/utils/boxChampGuidedSelectors';
import audioService from '../services/AudioService';
import * as RootNavigation from '../components/root_navigation';

const handoffOwnsFocus = getState =>
  typeof getState === 'function' && guidedHandoffOwnsFocus(getState());

export const getStateValues = (getState) => {
  const { prompt, currentOrder, currentItem, barcodeValue, typeScanState, typeScanAdjacentScans, activeBoxIndex, wmsZeroPending } = getState().scanPackScanning;
  const { userInfo, bothSettings } = getState().user;
  const scanPackSettings = bothSettings?.data?.data?.scanpack_setting;
  const generalSettings = bothSettings?.data?.data?.general_setting;
  const unScannedItems = currentOrder?.unscanned_items || [];
  const scannedItems = currentOrder?.scanned_items || [];
  return {
    prompt,
    currentOrder,
    currentItem,
    barcodeValue,
    userInfo,
    bothSettings,
    scanPackSettings,
    generalSettings,
    unScannedItems,
    scannedItems,
    typeScanState,
    typeScanAdjacentScans,
    wmsZeroPending,
    activeBoxIndex
  };
};

export const addLocalLog = (dispatch, logData) => {
  dispatch({ type: ADD_SCAN_PACK_LOCAL_LOG, payload: logData });
};

export const setScanPackLoadingState = (dispatch, val) => {
  dispatch({ type: SET_SCAN_PACK_LOADING, payload: val });
};

export const resetPromptWithError = (dispatch, getState) => {
  setScanPackLoadingState(dispatch, false);
  dispatch(setScanPackPromptInput(''));
  playErrorFeedback(getState);
};

export const navigateBack = (getState, preReadSource, scanPackParams) => {
  const navSource = preReadSource || getState().scanPackScanning.navigationSource;
  if (navSource?.from === 'scanToCart') {
    const routeParams = navSource.routeParams || {};
    RootNavigation.navigate('ScanToCart', {
      ...scanPackParams,
      resetCartFlag: true,
      ...(routeParams.postScanningContinue && {
        postScanningContinue: routeParams.postScanningContinue,
        postScanOrderId: routeParams.postScanOrderId,
        postScanningModulesRemaining: routeParams.postScanningModulesRemaining,
        postScanningToteInput: routeParams.postScanningToteInput
      })
    });
  } else {
    RootNavigation.reset('ScanPack', scanPackParams);
  }
};

export function setScanPackCurrentOrder(orderData) {
  return dispatch => {
    const restoredOrder = orderData ? restoreNotFulfilledItems(orderData) : orderData;
    dispatch({
      type: SET_SCAN_PACK_CURRENT_ORDER,
      payload: ensureOrderBoxFields(restoredOrder)
    });
  };
}

export function setScanPackCurrentItem(item) {
  return dispatch => {
    dispatch({
      type: SET_SCAN_PACK_CURRENT_ITEM,
      payload: item
    });
  };
}

export function setCurrentItem() {
  return (dispatch, getState) => {
    const { unScannedItems } = getStateValues(getState);
    let itemToSet = getFirstItem(unScannedItems);
    dispatch(setScanPackCurrentItem(itemToSet));
  };
}

export function shuffleToItem(itemToScan) {
  return (dispatch, getState) => {
    const { currentOrder } = getStateValues(getState);
    const shuffledOrder = shuffleItemToFront(currentOrder, itemToScan);
    dispatch(setScanPackCurrentOrder(shuffledOrder));
    dispatch(setScanPackCurrentItem(itemToScan));
  };
}

export function shuffleToItemBySku(sku, childSku = null) {
  return (dispatch, getState) => {
    const { currentOrder } = getStateValues(getState);
    const unScannedItems = currentOrder?.unscanned_items || [];
    const parent = unScannedItems.find(item => item.sku === sku);
    if (!parent) return;
    const itemToScan = childSku
      ? parent.child_items?.find(c => c.sku === childSku)
      : parent;
    if (itemToScan) dispatch(shuffleToItem(itemToScan));
  };
}

export function setScanPackBarcodeInput(barcodeValue) {
  return dispatch => {
    dispatch({
      type: SET_SCAN_PACK_BARCODE_INPUT,
      payload: barcodeValue
    });
  };
}

export function setScanPackMainInputFocus(hasFocus) {
  return dispatch => {
    dispatch({
      type: SET_SCAN_PACK_MAIN_INPUT_FOCUS,
      payload: hasFocus
    });
  };
}

export function setScanPackPrompt(isVisible, message = '', type = '', input = '', error = false) {
  return dispatch => {
    dispatch({
      type: SET_SCAN_PACK_PROMPT,
      payload: {
        isVisible,
        message,
        type,
        input,
        error
      }
    });
    if (isVisible) {
      dispatch(setScanPackMainInputFocus(false));
    }
  };
}

export function setScanPackPromptInput(input) {
  return (dispatch, getState) => {
    const { prompt } = getState().scanPackScanning;
    dispatch({
      type: SET_SCAN_PACK_PROMPT,
      payload: {
        ...prompt,
        input
      }
    });
  };
}

export function setScanPackScanSuccess(isSuccess, timeout = 120) {
  return dispatch => {
    dispatch({type: SET_SCAN_PACK_SCAN_SUCCESS, payload: isSuccess});
    setTimeout(() => {
      dispatch({type: SET_SCAN_PACK_SCAN_SUCCESS, payload: false});
    }, timeout);
  };
}

export function setScanPackScanError(hasError, timeout = 120) {
  return dispatch => {
    dispatch({type: SET_SCAN_PACK_SCAN_ERROR, payload: hasError});
    setTimeout(() => {
      dispatch({type: SET_SCAN_PACK_SCAN_ERROR, payload: false});
    }, timeout);
  };
}

export function setActiveBoxIndex(index) {
  return dispatch => {
    dispatch({ type: SET_ACTIVE_BOX_INDEX, payload: index });
  };
}

export function setMultiBoxAlert(visible, emptyBoxPrint = false) {
  return dispatch => {
    dispatch({ type: SET_MULTI_BOX_ALERT, payload: visible, emptyBoxPrint });
  };
}

let lastPrintSlipNoticeId = 0;

export const clearPrintSlipNotice = id => ({ type: CLEAR_PRINT_SLIP_NOTICE, payload: id });

export function showPrintSlipFailedNotice(timeout = 3000) {
  return dispatch => {
    lastPrintSlipNoticeId += 1;
    const id = lastPrintSlipNoticeId;
    dispatch({ type: SET_PRINT_SLIP_NOTICE, payload: id });
    setTimeout(() => dispatch(clearPrintSlipNotice(id)), timeout);
  };
}

export function setOrderComplete(flag) {
  return dispatch => {
    dispatch({ type: SET_ORDER_COMPLETE, payload: flag });
  };
}

export function setOrderCompleteAction(flag, timeout = 120) {
  return dispatch => {
    dispatch({ type: SET_ORDER_COMPLETE_ACTION, payload: flag });
    if (flag) {
      setTimeout(() => {
        dispatch({ type: SET_ORDER_COMPLETE_ACTION, payload: false });
      }, timeout);
    }
  };
}

export function setPostScanPipeline(pipeline) {
  return dispatch => {
    dispatch({ type: SET_POST_SCAN_PIPELINE, payload: pipeline });
  };
}

export function setPostScanTrackingInput(input) {
  return dispatch => {
    dispatch({ type: SET_POST_SCAN_TRACKING_INPUT, payload: input });
  };
}

export function setNavigationSource(source) {
  return dispatch => {
    dispatch({ type: SET_NAVIGATION_SOURCE, payload: source });
  };
}

export function closePostScanAlertBox(alertType) {
  return (dispatch, getState) => {
    const pipeline = getState().scanPackScanning.postScanPipeline;
    dispatch(setPostScanPipeline({
      ...pipeline,
      [alertType]: false
    }));
  };
}

export function setTypeScanState(sku) {
  return dispatch => {
    dispatch({
      type: SET_TYPE_SCAN_STATE,
      payload: sku
    });
  };
}

export function setTypeScanAdjacentScans(count) {
  return dispatch => {
    dispatch({
      type: SET_TYPE_SCAN_ADJACENT_SCANS,
      payload: count
    });
  };
}

export function closePrompt() {
  return (dispatch, getState) => {
    dispatch(setScanPackPrompt(false, '', '', '', false));
    if (!handoffOwnsFocus(getState)) dispatch(setScanPackMainInputFocus(true));
  };
}

// Carries the Z3 stop-and-receive ask's state across the chained escalation/lot/quantity prompts
// (see the reducer's `wmsZeroPending` doc comment). MUST be cleared via clearWmsZeroPending on
// both prompt cancel and successful commit.
export function setWmsZeroPending(payload) {
  return dispatch => {
    dispatch({ type: SET_WMS_ZERO_PENDING, payload });
  };
}

export function clearWmsZeroPending() {
  return dispatch => {
    dispatch({ type: SET_WMS_ZERO_PENDING, payload: null });
  };
}

export function setAliasState(payload) {
  return { type: SET_ALIAS_STATE, payload };
}

export function setBoxChampState(payload) {
  return { type: SET_BOX_CHAMP_STATE, payload };
}

export function resetBoxChampState() {
  return { type: RESET_BOX_CHAMP_STATE };
}

export function focusMainInputUnlessHandoff(hasFocus) {
  return (dispatch, getState) => {
    if (hasFocus && handoffOwnsFocus(getState)) return;
    dispatch(setScanPackMainInputFocus(hasFocus));
  };
}

export function handleInputFocus() {
  return (dispatch, getState) => {
    dispatch(setScanPackMainInputFocus(false));
    dispatch(setScanPackBarcodeInput(''));
    if (!handoffOwnsFocus(getState)) dispatch(setScanPackMainInputFocus(true));
  };
}

export const handleNotReady = async (dispatch, scanPackSettings) => {
  if (showsFailImage(scanPackSettings)) {
    dispatch(setScanPackScanError(true, getFailTimeout(scanPackSettings)));
  }
  if (scanPackSettings?.play_fail_sound !== false) await audioService.playNotReadySound();
  dispatch(handleInputFocus());
};
